import { z } from 'zod';
import {
  DEFAULT_PLATFORM,
  PLATFORMS,
  REGIONS,
  SERIES_FORMATS,
  SERIES_STATUSES,
  SIDES,
  TEAMS,
  USER_ROLES,
} from './enums.js';
import { MAX_RANK_TIER, MIN_RANK_TIER } from './ranks.js';
import { validateMapScore } from './score.js';

// ---------- primitives ----------

/** Any 8-4-4-4-12 hex id. valorant-api ids are not RFC 4122 compliant, so z.uuid() would reject them. */
export const uuidSchema = z.guid();
export const teamSchema = z.enum(TEAMS);
export const sideSchema = z.enum(SIDES);
export const roleSchema = z.enum(USER_ROLES);
export const seriesFormatSchema = z.enum(SERIES_FORMATS);
export const seriesStatusSchema = z.enum(SERIES_STATUSES);
export const regionSchema = z.enum(REGIONS);
export const platformSchema = z.enum(PLATFORMS);

export const rankTierSchema = z.int().min(MIN_RANK_TIER).max(MAX_RANK_TIER);

export const usernameSchema = z
  .string()
  .trim()
  .min(3, 'Mindestens 3 Zeichen')
  .max(32, 'Maximal 32 Zeichen')
  .regex(/^[a-zA-Z0-9_.-]+$/, 'Nur Buchstaben, Zahlen, _ . -');

export const passwordSchema = z
  .string()
  .min(10, 'Mindestens 10 Zeichen')
  .max(128, 'Maximal 128 Zeichen');

export const emailSchema = z
  .string()
  .trim()
  .toLowerCase()
  .pipe(z.email('Ungültige E-Mail-Adresse'))
  .pipe(z.string().max(254));

export const riotNameSchema = z
  .string()
  .trim()
  .min(3, 'Riot-Name: mindestens 3 Zeichen')
  .max(16, 'Riot-Name: maximal 16 Zeichen')
  .refine((v) => !v.includes('#'), 'Riot-Name ohne #Tag angeben');

export const riotTagSchema = z
  .string()
  .trim()
  .regex(/^[\p{L}\p{N}]{3,5}$/u, 'Tag: 3–5 Buchstaben/Zahlen ohne #');

export const displayNameSchema = z
  .string()
  .trim()
  .min(1, 'Name erforderlich')
  .max(32, 'Maximal 32 Zeichen');
export const teamNameSchema = z
  .string()
  .trim()
  .min(1, 'Teamname erforderlich')
  .max(32, 'Maximal 32 Zeichen');

// ---------- auth ----------

export const loginSchema = z.object({
  /** Username or e-mail. */
  login: z.string().trim().min(1, 'Benutzername oder E-Mail erforderlich').max(254),
  password: z.string().min(1, 'Passwort erforderlich').max(128),
});
export type LoginInput = z.infer<typeof loginSchema>;

export const registerSchema = z.object({
  inviteToken: z.string().min(16).max(256),
  username: usernameSchema,
  email: emailSchema,
  password: passwordSchema,
});
export type RegisterInput = z.infer<typeof registerSchema>;

export const changePasswordSchema = z.object({
  currentPassword: z.string().min(1).max(128),
  newPassword: passwordSchema,
});
export type ChangePasswordInput = z.infer<typeof changePasswordSchema>;

// ---------- users & invites ----------

export const updateUserSchema = z.object({
  role: roleSchema,
});
export type UpdateUserInput = z.infer<typeof updateUserSchema>;

export const createInviteSchema = z.object({
  role: roleSchema.default('player'),
  /** Validity in hours (1 h – 30 days). */
  expiresInHours: z
    .int()
    .min(1)
    .max(24 * 30)
    .default(72),
  /** Optional existing player that gets linked to the new account. */
  playerId: uuidSchema.nullish(),
});
export type CreateInviteInput = z.input<typeof createInviteSchema>;

// ---------- players ----------

const playerFields = {
  displayName: displayNameSchema,
  riotName: riotNameSchema.nullish(),
  riotTag: riotTagSchema.nullish(),
  region: regionSchema.default('eu'),
  platform: platformSchema.default(DEFAULT_PLATFORM),
  currentRankTier: rankTierSchema.nullish(),
  peakRankTier: rankTierSchema.nullish(),
  avatarAgentId: uuidSchema.nullish(),
};

const riotIdComplete = (v: { riotName?: string | null; riotTag?: string | null }) =>
  (v.riotName == null) === (v.riotTag == null);
const riotIdMessage = { message: 'Riot-Name und Tag nur gemeinsam angeben', path: ['riotTag'] };

const peakNotBelowCurrent = (v: {
  currentRankTier?: number | null;
  peakRankTier?: number | null;
}) => v.currentRankTier == null || v.peakRankTier == null || v.peakRankTier >= v.currentRankTier;
const peakMessage = {
  message: 'Peak-Rank darf nicht unter dem aktuellen Rank liegen',
  path: ['peakRankTier'],
};

export const createPlayerSchema = z
  .object(playerFields)
  .refine(riotIdComplete, riotIdMessage)
  .refine(peakNotBelowCurrent, peakMessage);
export type CreatePlayerInput = z.input<typeof createPlayerSchema>;

export const updatePlayerSchema = z
  .object({
    displayName: displayNameSchema.optional(),
    riotName: riotNameSchema.nullish(),
    riotTag: riotTagSchema.nullish(),
    region: regionSchema.optional(),
    platform: platformSchema.optional(),
    currentRankTier: rankTierSchema.nullish(),
    peakRankTier: rankTierSchema.nullish(),
    avatarAgentId: uuidSchema.nullish(),
  })
  .refine(
    (v) =>
      (v.riotName === undefined) === (v.riotTag === undefined) &&
      (v.riotName === undefined || riotIdComplete(v)),
    riotIdMessage,
  )
  .refine(peakNotBelowCurrent, peakMessage);
export type UpdatePlayerInput = z.input<typeof updatePlayerSchema>;

export const linkPlayerSchema = z.object({
  userId: uuidSchema.nullable(),
});
export type LinkPlayerInput = z.infer<typeof linkPlayerSchema>;

// ---------- assets ----------

export const updateMapSchema = z.object({
  inPool: z.boolean(),
});
export type UpdateMapInput = z.infer<typeof updateMapSchema>;

// ---------- settings ----------

export const updateSettingsSchema = z.object({
  currentRankWeight: z.number().min(0).max(1).optional(),
  vetoStepSeconds: z.int().min(10).max(300).optional(),
  /** null removes a stored key. Ignored when the key is configured via ENV. */
  henrikApiKey: z.string().trim().min(10).max(200).nullable().optional(),
});
export type UpdateSettingsInput = z.infer<typeof updateSettingsSchema>;

// ---------- balancing ----------

const playerPairSchema = z
  .tuple([uuidSchema, uuidSchema])
  .refine(([x, y]) => x !== y, 'Ein Paar braucht zwei verschiedene Spieler');

export const balancingRequestSchema = z.object({
  playerIds: z
    .array(uuidSchema)
    .min(2)
    .max(12)
    .refine((ids) => ids.length % 2 === 0, 'Gerade Anzahl Spieler erforderlich')
    .refine((ids) => new Set(ids).size === ids.length, 'Spieler doppelt ausgewählt'),
  together: z.array(playerPairSchema).max(10).default([]),
  apart: z.array(playerPairSchema).max(10).default([]),
  limit: z.int().min(1).max(10).default(3),
});
export type BalancingRequest = z.input<typeof balancingRequestSchema>;

// ---------- series ----------

export const seriesPlayerSchema = z.object({
  playerId: uuidSchema,
  team: teamSchema,
});

export const createSeriesSchema = z
  .object({
    format: seriesFormatSchema,
    teamAName: teamNameSchema,
    teamBName: teamNameSchema,
    players: z.array(seriesPlayerSchema).min(2).max(10),
    teamAIglPlayerId: uuidSchema.nullish(),
    teamBIglPlayerId: uuidSchema.nullish(),
  })
  .superRefine((v, ctx) => {
    const ids = v.players.map((p) => p.playerId);
    if (new Set(ids).size !== ids.length) {
      ctx.addIssue({ code: 'custom', message: 'Spieler doppelt ausgewählt', path: ['players'] });
    }
    const countA = v.players.filter((p) => p.team === 'a').length;
    const countB = v.players.length - countA;
    if (countA !== countB) {
      ctx.addIssue({
        code: 'custom',
        message: 'Beide Teams brauchen gleich viele Spieler',
        path: ['players'],
      });
    }
    if (countA > 5) {
      ctx.addIssue({ code: 'custom', message: 'Maximal 5 Spieler pro Team', path: ['players'] });
    }
    if (v.teamAName.toLowerCase() === v.teamBName.toLowerCase()) {
      ctx.addIssue({
        code: 'custom',
        message: 'Teamnamen müssen sich unterscheiden',
        path: ['teamBName'],
      });
    }
    const check = (iglId: string | null | undefined, team: 'a' | 'b', path: string) => {
      if (iglId && !v.players.some((p) => p.playerId === iglId && p.team === team)) {
        ctx.addIssue({ code: 'custom', message: 'IGL muss im eigenen Team spielen', path: [path] });
      }
    };
    check(v.teamAIglPlayerId, 'a', 'teamAIglPlayerId');
    check(v.teamBIglPlayerId, 'b', 'teamBIglPlayerId');
  });
export type CreateSeriesInput = z.input<typeof createSeriesSchema>;

export const listSeriesQuerySchema = z.object({
  status: seriesStatusSchema.optional(),
  playerId: uuidSchema.optional(),
  limit: z.coerce.number().int().min(1).max(100).default(20),
  offset: z.coerce.number().int().min(0).default(0),
});
export type ListSeriesQuery = z.infer<typeof listSeriesQuerySchema>;

export const mapResultPlayerSchema = z.object({
  playerId: uuidSchema,
  agentId: uuidSchema,
  kills: z.int().min(0).max(200).nullish(),
  deaths: z.int().min(0).max(200).nullish(),
  assists: z.int().min(0).max(200).nullish(),
  acs: z.int().min(0).max(1000).nullish(),
});

export const mapResultSchema = z
  .object({
    scoreA: z.int().min(0).max(99),
    scoreB: z.int().min(0).max(99),
    players: z.array(mapResultPlayerSchema).max(10),
    externalMatchId: z.string().trim().min(1).max(100).nullish(),
    playedAt: z.iso.datetime({ offset: true }).nullish(),
  })
  .superRefine((v, ctx) => {
    const result = validateMapScore(v.scoreA, v.scoreB);
    if (!result.valid) ctx.addIssue({ code: 'custom', message: result.error, path: ['scoreA'] });
    const ids = v.players.map((p) => p.playerId);
    if (new Set(ids).size !== ids.length) {
      ctx.addIssue({ code: 'custom', message: 'Spieler doppelt eingetragen', path: ['players'] });
    }
  });
export type MapResultInput = z.input<typeof mapResultSchema>;

// ---------- veto (socket payloads) ----------

export const vetoJoinSchema = z.object({ seriesId: uuidSchema });
export const vetoReadySchema = z.object({
  seriesId: uuidSchema,
  /** Organizers/admins can set ready on behalf of a team. */
  team: teamSchema.optional(),
});
export const vetoActionSchema = z.object({ seriesId: uuidSchema, mapId: uuidSchema });
export const vetoSideSchema = z.object({ seriesId: uuidSchema, side: sideSchema });

// ---------- stats ----------

export const headToHeadQuerySchema = z
  .object({ playerA: uuidSchema, playerB: uuidSchema })
  .refine((v) => v.playerA !== v.playerB, 'Zwei verschiedene Spieler wählen');

// ---------- integrations ----------

export const henrikMatchesQuerySchema = z.object({
  playerId: uuidSchema,
});
